import React, { useState } from 'react';
import { Send, User, Mail, Building2, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import type { EligibleTraining } from '@/data/eligibleTrainings';

interface Props {
  training: EligibleTraining;
  fr: boolean;
}

export const TrainingContactForm: React.FC<Props> = ({ training, fr }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(fr ? 'Veuillez remplir tous les champs obligatoires.' : 'Please fill in all required fields.');
      return;
    }
    setIsSubmitting(true);
    // Simulate submission
    await new Promise(r => setTimeout(r, 1000));
    toast.success(fr ? 'Votre demande a bien été envoyée. Nous vous recontactons sous 24h.' : 'Your request has been sent. We\'ll get back to you within 24h.');
    setForm({ name: '', email: '', company: '', message: '' });
    setIsSubmitting(false);
  };

  return (
    <section id="contact-formation" className="py-16 bg-secondary/30">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl font-bold text-foreground mb-2 text-center">
          {fr ? 'Demander des informations' : 'Request information'}
        </h2>
        <p className="text-muted-foreground text-center mb-8">
          {fr
            ? `À propos de la formation « ${training.title} »`
            : `About the "${training.title}" program`}
        </p>

        <Card className="border-border">
          <CardContent className="p-6 sm:p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact-name" className="flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5 text-primary" />
                    {fr ? 'Nom complet *' : 'Full name *'}
                  </Label>
                  <Input
                    id="contact-name"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder={fr ? 'Jean Dupont' : 'John Doe'}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-email" className="flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5 text-primary" />
                    {fr ? 'Email professionnel *' : 'Professional email *'}
                  </Label>
                  <Input
                    id="contact-email"
                    type="email"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    placeholder={fr ? 'jean@cabinet.fr' : 'john@firm.com'}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact-company" className="flex items-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5 text-primary" />
                  {fr ? 'Cabinet / Organisation' : 'Firm / Organization'}
                </Label>
                <Input
                  id="contact-company"
                  value={form.company}
                  onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
                  placeholder={fr ? 'Cabinet Dupont & Associés' : 'Dupont & Associates'}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact-message" className="flex items-center gap-1.5">
                  <MessageSquare className="w-3.5 h-3.5 text-primary" />
                  {fr ? 'Votre message *' : 'Your message *'}
                </Label>
                <Textarea
                  id="contact-message"
                  value={form.message}
                  onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                  placeholder={fr ? 'Je souhaite obtenir plus d\'informations sur cette formation...' : 'I would like more information about this program...'}
                  rows={4}
                  required
                />
              </div>

              <Button type="submit" className="w-full" size="lg" disabled={isSubmitting}>
                <Send className="w-4 h-4 mr-2" />
                {isSubmitting
                  ? (fr ? 'Envoi en cours...' : 'Sending...')
                  : (fr ? 'Envoyer ma demande' : 'Send my request')}
              </Button>

              <p className="text-xs text-muted-foreground text-center">
                {fr
                  ? 'Vos données sont traitées conformément au RGPD. Réponse sous 24h ouvrées.'
                  : 'Your data is processed in compliance with GDPR. Response within 24 business hours.'}
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};
